/**
 * Advanced Theme Provider Helper Functions
 * 
 * Helper functions and context for advanced theme provider including:
 * - AdvancedThemeContext creation
 * - useAdvancedTheme hook
 * - Theme palette definitions (standard, cyberpunk, neon, aurora, holographic)
 * - Theme creation utilities (createAdvancedDarkTheme, createAdvancedLightTheme)
 * 
 * @module components/common/AdvancedThemeProvider.helpers
 */

import React, { createContext, useContext } from 'react';
import { createTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

export const AdvancedThemeContext = createContext();

export const useAdvancedTheme = () => {
  const { _t } = useTranslation();
  const context = useContext(AdvancedThemeContext);
  if (!context) {
    throw new Error('useAdvancedTheme must be used within AdvancedThemeProvider');
  }
  return context;
};

export const THEME_PALETTES = {
  standard: {
    primary: '#1976d2',
    secondary: '#dc004e',
    accent: '#2196f3',
    background: '#ffffff',
    surface: '#f5f5f5',
    glow: 'rgba(25, 118, 210, 0.1)'
  },
  cyberpunk: {
    primary: '#00ffff',
    secondary: '#ff00ff',
    accent: '#ffff00',
    background: '#0a0a0a',
    surface: '#1a1a1a',
    glow: 'rgba(0, 255, 255, 0.3)'
  },
  neon: {
    primary: '#ff0080',
    secondary: '#00ff80',
    accent: '#8000ff',
    background: '#0d0d0d',
    surface: '#1f1f1f',
    glow: 'rgba(255, 0, 128, 0.3)'
  },
  aurora: {
    primary: '#00ff9f',
    secondary: '#ff6b9d',
    accent: '#4ecdc4',
    background: '#0f0f23',
    surface: '#1a1a3a',
    glow: 'rgba(0, 255, 159, 0.3)'
  },
  holographic: {
    primary: '#7c3aed',
    secondary: '#06b6d4',
    accent: '#f59e0b',
    background: '#0c0c0c',
    surface: '#1c1c1c',
    glow: 'rgba(124, 58, 237, 0.3)'
  }
};

export const createAdvancedDarkTheme = (palette) => createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: palette.primary,
      light: palette.primary + '80',
      dark: palette.primary + '40',
    },
    secondary: {
      main: palette.secondary,
      light: palette.secondary + '80',
      dark: palette.secondary + '40',
    },
    background: {
      default: palette.background,
      paper: palette.surface,
    },
    text: {
      primary: '#ffffff',
      secondary: '#b0b0b0',
    },
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          background: `linear-gradient(135deg, ${palette.surface}80, ${palette.surface}40)`,
          backdropFilter: 'blur(20px)',
          border: `1px solid ${palette.primary}20`,
          borderRadius: '16px',
          boxShadow: `0 8px 32px ${palette.glow}`,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-8px)',
            boxShadow: `0 16px 48px ${palette.glow}, 0 0 20px ${palette.primary}40`,
            border: `1px solid ${palette.primary}40`,
          }
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          background: `linear-gradient(45deg, ${palette.primary}20, ${palette.secondary}20)`,
          border: `1px solid ${palette.primary}40`,
          borderRadius: '12px',
          color: palette.primary,
          textShadow: `0 0 10px ${palette.primary}80`,
          transition: 'all 0.3s ease',
          '&:hover': {
            background: `linear-gradient(45deg, ${palette.primary}40, ${palette.secondary}40)`,
            boxShadow: `0 0 20px ${palette.primary}60`,
            transform: 'scale(1.05)',
          }
        }
      }
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          background: `linear-gradient(135deg, ${palette.surface}60, ${palette.surface}20)`,
          backdropFilter: 'blur(10px)',
          border: `1px solid ${palette.accent}30`,
          borderRadius: '10px',
          color: '#ffffff',
          '&:hover': {
            transform: 'translateX(8px)',
            boxShadow: `0 4px 16px ${palette.accent}40`,
          }
        }
      }
    },
    MuiTypography: {
      styleOverrides: {
        h6: {
          background: `linear-gradient(45deg, ${palette.primary}, ${palette.secondary})`,
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          fontWeight: 700,
          textShadow: `0 0 20px ${palette.primary}40`,
        }
      }
    }
  }
});

export const createAdvancedLightTheme = () => createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#2563eb',
    },
    secondary: {
      main: '#7c3aed',
    },
    background: {
      default: '#f8fafc',
      paper: '#ffffff',
    },
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          background: 'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          borderRadius: '16px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 16px 48px rgba(0, 0, 0, 0.15)',
          }
        }
      }
    }
  }
});
